import { createVendorIfNeeded, supabase, uploadReceiptFile } from "./expenseService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

async function accessToken() {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) throw new Error("Please sign in again");
  return data.session.access_token;
}

export async function getOcrKeyStatus() {
  const token = await accessToken();
  const response = await fetch(`${BACKEND_URL}/api/ocr/key`, { headers: { Authorization: `Bearer ${token}` } });
  if (!response.ok) throw new Error("Could not read OCR key status");
  return response.json();
}

export async function saveOcrKey(apiKey) {
  const token = await accessToken();
  const response = await fetch(`${BACKEND_URL}/api/ocr/key`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ api_key: apiKey }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || "Could not save OpenAI key");
  return payload;
}

export async function extractReceiptData(file) {
  const token = await accessToken();
  const formData = new FormData();
  formData.append("file", file);
  const response = await fetch(`${BACKEND_URL}/api/ocr/extract`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || "OCR extraction failed");
  return payload;
}

export function parseLineItems(raw) {
  return (raw?.line_items || []).map((item, index) => ({
    client_id: `${Date.now()}-${index}`,
    description: item.description || "Receipt item",
    quantity: Number(item.quantity || 1),
    unit_price: Number(item.unit_price || 0),
    line_total: Number(item.line_total || 0),
    suggested_category_name: item.suggested_category_name || "Other",
    confidence_score: Number(item.confidence_score || 0.75),
    category_id: "",
    property_id: "",
    is_approved: false,
  }));
}

export function suggestCategories(lineItems, categories) {
  return lineItems.map((line) => {
    const match = categories.find((cat) => cat.name.toLowerCase() === line.suggested_category_name.toLowerCase());
    return { ...line, category_id: line.category_id || match?.id || "" };
  });
}

export async function uploadReceipt(file, userId, propertyId) {
  return uploadReceiptFile({ file, userId, propertyId });
}

export async function createDraftExpenseFromReceipt({ userId, receipt, extraction, propertyId, categories }) {
  const vendor = await createVendorIfNeeded(userId, extraction.vendor_name);
  await supabase
    .from("receipts")
    .update({
      property_id: propertyId || null,
      vendor_id: vendor.id,
      receipt_date: extraction.receipt_date,
      vendor_name_extracted: extraction.vendor_name,
      subtotal: Number(extraction.subtotal || 0),
      tax_amount: Number(extraction.tax || 0),
      tip_amount: Number(extraction.tip || 0),
      total_amount: Number(extraction.total || 0),
      payment_method_text: extraction.payment_method || "",
      raw_ocr_text: extraction.raw_ocr_text || JSON.stringify(extraction),
      ocr_provider: extraction.provider || "OpenAI Vision",
      extraction_status: extraction.mocked ? "MOCKED" : "Extracted",
      review_status: "Needs Review",
    })
    .eq("id", receipt.id);

  const lines = suggestCategories(parseLineItems(extraction), categories).map((line) => ({
    user_id: userId,
    receipt_id: receipt.id,
    property_id: propertyId || null,
    category_id: line.category_id || null,
    description: line.description,
    quantity: line.quantity,
    unit_price: line.unit_price,
    line_total: line.line_total,
    suggested_category_name: line.suggested_category_name,
    confidence_score: line.confidence_score,
    is_approved: false,
  }));
  const { data, error } = await supabase.from("receipt_line_items").insert(lines).select("*");
  if (error) throw error;
  return { vendor, lines: data || [] };
}

export async function approveReceiptLines({ userId, receiptId, vendorId, receiptDate, lines, notes }) {
  const created = [];
  for (const line of lines) {
    const { data, error } = await supabase
      .from("expenses")
      .insert({
        user_id: userId,
        receipt_id: receiptId,
        vendor_id: vendorId || null,
        property_id: line.property_id || null,
        category_id: line.category_id || null,
        expense_date: receiptDate || new Date().toISOString().slice(0, 10),
        description: line.description,
        amount: Number(line.line_total || 0),
        tax_amount: 0,
        tip_amount: 0,
        is_tax_deductible: true,
        is_reimbursable: false,
        schedule_e_category: line.schedule_e_category || "",
        status: "Categorized",
        notes: notes || "Created from scanned receipt",
      })
      .select("*")
      .single();
    if (error) throw error;
    created.push(data);
    if (line.id) {
      await supabase.from("receipt_line_items").update({ is_approved: true, created_expense_id: data.id }).eq("id", line.id);
    }
  }
  await supabase.from("receipts").update({ review_status: "Reviewed" }).eq("id", receiptId);
  return created;
}